import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "../../../lib/utils";
import { Button } from "../../../components/ui/button";
import { Card } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import AppShell from "../../../components/app-shell/AppShell";
import { supabase } from "../../../supabaseClient.jsx";
import { useCart } from "../../../shared/contexts/CartContext.jsx";
import { usePaymentGateway } from "../../payment/usePaymentGateway.js";
import { normalizePaymentStatus } from "../../payment/paymentStatus.js";
import { hasActiveSubscription } from "../../payment/subscription/subscriptionStatus.js";
import DocumentsViewer from "../../../shared/documents/DocumentsViewer.jsx";
import CardSolicitacao from "../../../components/CardSolicitacao.jsx";
import ModalAvaliacao from "../../../components/ModalAvaliacao.jsx";
import SuportePage from "../support/SuportePage.jsx";

const PAYMENT_STATUS_BADGE = {
  aprovado:  "default",
  pendente:  "secondary",
  falhou:    "destructive",
  cancelado: "outline",
  estornado: "outline",
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value ?? 0);

export default function DashboardFamilyPage() {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { gateway } = usePaymentGateway();
  const [activeTab, setActiveTab] = useState("inicio");
  const [pacotesAtivos, setPacotesAtivos] = useState([]);
  const [planosAtivos, setPlanosAtivos] = useState([]);
  const [loadingPacotes, setLoadingPacotes] = useState(true);
  const [loadingPlanos, setLoadingPlanos] = useState(true);
  const [payments, setPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [subscriptionActive, setSubscriptionActive] = useState(false);
  const [loadingSubscriptionStatus, setLoadingSubscriptionStatus] = useState(true);
  const [userId, setUserId] = useState(null);
  const [perfil, setPerfil] = useState({
    nome: "Família",
    sobrenome: "Silva",
    foto_url_display: null,
    email: "",
  });
  const [editingPerfil, setEditingPerfil] = useState(false);
  const [savingPerfil, setSavingPerfil] = useState(false);
  const [perfilForm, setPerfilForm] = useState({ nome: "", sobrenome: "" });
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [loadingSolicitacoes, setLoadingSolicitacoes] = useState(true);
  const [avaliadas, setAvaliadas] = useState(() => new Set());
  const [modalAval, setModalAval] = useState({ open: false, solicitacao: null });

  const approvedPayments = payments.filter((p) => p.status === "aprovado");
  const pendingPayments = payments.filter((p) => p.status === "pendente");
  const failedPayments = payments.filter((p) => p.status === "falhou");
  const totalApproved = approvedPayments.reduce((acc, p) => acc + (p.total || 0), 0);
  const totalPending = pendingPayments.reduce((acc, p) => acc + (p.total || 0), 0);

  useEffect(() => {
    async function bootstrapDashboard() {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      const resolvedUserId = user?.id || null;
      setUserId(resolvedUserId);

      setLoadingSubscriptionStatus(true);
      if (user?.id) {
        const active = await hasActiveSubscription({
          userId: user.id,
          email: user.email,
        });
        setSubscriptionActive(active);
      } else {
        setSubscriptionActive(false);
      }
      setLoadingSubscriptionStatus(false);

      if (user?.id) {
        const { data } = await supabase
          .from("usuarios")
          .select("nome, sobrenome, email, foto_url, documentos_url")
          .eq("id", user.id)
          .maybeSingle();

        if (data) {
          const nomeResolved = data.nome || "Família";
          const sobrenomeResolved = data.sobrenome || "Silva";
          setPerfil((prev) => ({
            ...prev,
            nome: nomeResolved,
            sobrenome: sobrenomeResolved,
            email: data.email || "",
            foto_url: data.foto_url || null,
            documentos_url: data.documentos_url || null,
          }));
          setPerfilForm({
            nome: nomeResolved,
            sobrenome: sobrenomeResolved,
          });
        } else {
          setPerfil((prev) => ({ ...prev, email: user.email || "" }));
          setPerfilForm((prev) => ({
            nome: prev.nome || "Família",
            sobrenome: prev.sobrenome || "Silva",
          }));
        }
      } else {
        setPerfilForm({ nome: "Família", sobrenome: "Silva" });
      }

      setLoadingPacotes(true);
      const { data: pacotesData } = await supabase
        .from("produtos")
        .select("id, nome, valor, horas")
        .eq("tipo", "pacote_horas")
        .eq("ativo", true)
        .order("valor", { ascending: true })
        .limit(3);

      setPacotesAtivos(pacotesData || []);
      setLoadingPacotes(false);

      setLoadingPlanos(true);
      const { data: planosData } = await supabase
        .from("produtos")
        .select("id, nome, valor, descricao")
        .eq("tipo", "assinatura")
        .eq("ativo", true)
        .order("valor", { ascending: true })
        .limit(3);

      setPlanosAtivos(planosData || []);
      setLoadingPlanos(false);

      setLoadingPayments(true);
      if (resolvedUserId) {
        const result = await gateway.listPayments({ userId: resolvedUserId });
        setPayments(result || []);
      } else {
        setPayments([]);
      }
      setLoadingPayments(false);
    }

    bootstrapDashboard();
  }, [gateway]);

  const carregarSolicitacoes = useCallback(async (uid) => {
    if (!uid) {
      setSolicitacoes([]);
      setLoadingSolicitacoes(false);
      return;
    }
    setLoadingSolicitacoes(true);

    const { data: sols } = await supabase
      .from("solicitacoes_familia")
      .select("id, baba_id, status, criado_em, mensagem")
      .eq("familia_id", uid)
      .order("criado_em", { ascending: false });

    const lista = sols || [];

    // Enriquece com nome/foto da babá a partir da view pública (sem contato).
    const babaIds = [...new Set(lista.map((s) => s.baba_id).filter(Boolean))];
    const babasMap = {};
    if (babaIds.length) {
      const { data: babas } = await supabase
        .from("view_babas_publicas")
        .select("id, nome, sobrenome, foto_url")
        .in("id", babaIds);
      (babas || []).forEach((b) => {
        babasMap[b.id] = {
          nome: [b.nome, b.sobrenome].filter(Boolean).join(" ") || "Babá",
          foto_url: b.foto_url,
        };
      });
    }

    const { data: avals } = await supabase
      .from("avaliacoes")
      .select("solicitacao_id")
      .eq("familia_id", uid);
    setAvaliadas(new Set((avals || []).map((a) => a.solicitacao_id)));

    setSolicitacoes(
      lista.map((s) => ({
        ...s,
        baba_nome: babasMap[s.baba_id]?.nome || "Babá",
        baba_foto_url: babasMap[s.baba_id]?.foto_url || null,
      })),
    );
    setLoadingSolicitacoes(false);
  }, []);

  useEffect(() => {
    carregarSolicitacoes(userId);
  }, [userId, carregarSolicitacoes]);

  const abrirAvaliacao = (sol) =>
    setModalAval({
      open: true,
      solicitacao: {
        id: sol.id,
        baba_id: sol.baba_id,
        baba_nome: sol.baba_nome,
        baba_foto_url: sol.baba_foto_url,
      },
    });

  const handleAvaliada = (solicitacaoId) => {
    setAvaliadas((prev) => new Set(prev).add(solicitacaoId));
  };

  const renderSolicitacoesList = (limite) => {
    if (loadingSolicitacoes) {
      return <p className="text-sm text-muted-foreground">Carregando contratações...</p>;
    }
    if (solicitacoes.length === 0) {
      return (
        <div className="text-sm text-muted-foreground">
          <p className="mb-3">
            Você ainda não solicitou nenhuma babá. Encontre a babá ideal no catálogo!
          </p>
          <Button onClick={() => navigate("/babas")}>Ver babás</Button>
        </div>
      );
    }
    const itens = limite ? solicitacoes.slice(0, limite) : solicitacoes;
    return (
      <div className="space-y-3">
        {itens.map((s) => (
          <CardSolicitacao
            key={s.id}
            solicitacao={s}
            jaAvaliada={avaliadas.has(s.id)}
            onAvaliar={abrirAvaliacao}
          />
        ))}
        {limite && solicitacoes.length > limite && (
          <Button variant="outline" onClick={() => setActiveTab("contratacoes")}>
            Ver todas ({solicitacoes.length})
          </Button>
        )}
      </div>
    );
  };

  const handleSavePerfil = async () => {
    if (!userId) {
      setEditingPerfil(false);
      setPerfil((prev) => ({
        ...prev,
        nome: perfilForm.nome || prev.nome,
        sobrenome: perfilForm.sobrenome || prev.sobrenome,
      }));
      return;
    }

    setSavingPerfil(true);
    const { error } = await supabase
      .from("usuarios")
      .update({
        nome: perfilForm.nome.trim(),
        sobrenome: perfilForm.sobrenome.trim(),
      })
      .eq("id", userId);

    if (!error) {
      setPerfil((prev) => ({
        ...prev,
        nome: perfilForm.nome.trim() || prev.nome,
        sobrenome: perfilForm.sobrenome.trim() || prev.sobrenome,
      }));
      setEditingPerfil(false);
    }
    setSavingPerfil(false);
  };

  const handleAssinarPlano = (plano) => {
    addItem({
      id: plano.id,
      tipo: "assinatura",
      nome: plano.nome,
      preco: plano.valor || 0,
      descricao: plano.descricao,
    });
    navigate("/pagamento");
  };

  const renderPacotesList = () => {
    if (loadingPacotes) {
      return <p className="text-sm text-muted-foreground">Carregando pacotes...</p>;
    }

    if (pacotesAtivos.length === 0) {
      return <p className="text-sm text-muted-foreground">Não há pacotes ativos no momento.</p>;
    }

    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
        {pacotesAtivos.map((pacote) => (
          <div key={pacote.id} className="rounded-xl border border-border p-4">
            <p className="text-sm font-semibold text-foreground">{pacote.nome}</p>
            <p className="text-xs text-muted-foreground mt-1">{pacote.horas || 0}h</p>
            <p className="text-sm font-bold text-foreground mt-2">
              {formatCurrency((pacote.valor || 0) * (pacote.horas || 0))}
            </p>
            <Button
              className="w-full mt-3"
              onClick={() => navigate(`/checkout/pacote/${pacote.id}`)}
            >
              Ir para checkout
            </Button>
          </div>
        ))}
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case "inicio":
      case "agendamentos":
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="p-6 md:col-span-2">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-foreground">
                    Status da assinatura
                  </h3>
                  {loadingSubscriptionStatus ? (
                    <p className="mt-1 text-sm text-muted-foreground">Verificando assinatura...</p>
                  ) : subscriptionActive ? (
                    <p className="mt-1 text-sm text-emerald-600">
                      Assinatura ativa. Sua familia pode comprar pacotes normalmente.
                    </p>
                  ) : (
                    <p className="mt-1 text-sm text-amber-600">
                      Assinatura inativa. Escolha uma assinatura para liberar compra de pacotes.
                    </p>
                  )}
                </div>

                {!loadingSubscriptionStatus && !subscriptionActive && (
                  <Button onClick={() => navigate("/escolher-assinatura?next=/dashboard-familia")}>Resolver pendencia</Button>
                )}
              </div>
            </Card>

            <Card className="p-6 md:col-span-2">
              <div className="flex items-center justify-between gap-3 mb-3">
                <h3 className="text-lg font-semibold text-foreground">
                  Minhas contratações
                </h3>
                {solicitacoes.length > 0 && (
                  <Button variant="outline" onClick={() => setActiveTab("contratacoes")}>
                    Ver todas
                  </Button>
                )}
              </div>
              {renderSolicitacoesList(3)}
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Agendamentos
              </h3>
              <p className="text-sm text-muted-foreground">
                Visualize e gerencie seus próximos serviços
              </p>
              <p className="mt-3 text-xs text-muted-foreground">
                Total de pagamentos aprovados: {approvedPayments.length}
              </p>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    Pagamentos
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Finalize compras e acompanhe seu historico
                  </p>
                </div>
                <Button variant="outline" onClick={() => navigate("/meus-pagamentos")}>Ver historico</Button>
              </div>
            </Card>

            <Card className="p-6 md:col-span-2">
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Pacotes ativos
              </h3>
              {renderPacotesList()}
            </Card>
          </div>
        );
      case "contratacoes":
        return (
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-foreground mb-1">
              Minhas contratações
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Acompanhe o status das babás que você solicitou. Nossa equipe
              intermedia todo o contato.
            </p>
            {renderSolicitacoesList()}
          </Card>
        );
      case "suporte":
        return <SuportePage familiaId={userId} />;
      case "buscar":
      case "pacotes":
        return (
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Pacotes
            </h3>
            <p className="text-sm text-muted-foreground">
              Gerencie seus pacotes e siga para checkout.
            </p>
            {renderPacotesList()}
          </Card>
        );
      case "historico":
        return (
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Histórico de pagamentos
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Consulte suas transações e recibos.
            </p>
            {loadingPayments ? (
              <p className="text-sm text-muted-foreground mb-4">Carregando pagamentos...</p>
            ) : payments.length === 0 ? (
              <p className="text-sm text-muted-foreground mb-4">Nenhum pagamento encontrado.</p>
            ) : (
              <div className="mb-4 space-y-2">
                {payments.slice(0, 5).map((payment) => (
                  <div key={payment.id} className="rounded-lg border border-border px-3 py-2 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-medium text-foreground">{payment.items?.[0]?.nome || "Pagamento"}</span>
                      <span className="text-foreground">{formatCurrency(payment.total)}</span>
                    </div>
                    <div className="mt-1">
                      <Badge variant={PAYMENT_STATUS_BADGE[normalizePaymentStatus(payment.status)] ?? "secondary"}>
                        {normalizePaymentStatus(payment.status)}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <Button onClick={() => navigate("/meus-pagamentos")}>Abrir histórico</Button>
          </Card>
        );
      case "assinaturas":
      case "planos":
      case "extrato":
        return (
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              {activeTab === "extrato" ? "Extrato" : "Planos"}
            </h3>
            {activeTab === "extrato" ? (
              <div className="space-y-3">
                <div className="rounded-lg border border-border p-3 text-sm">
                  <p className="text-muted-foreground">Total aprovado</p>
                  <p className="font-bold text-foreground">{formatCurrency(totalApproved)}</p>
                </div>
                <div className="rounded-lg border border-border p-3 text-sm">
                  <p className="text-muted-foreground">Total pendente</p>
                  <p className="font-bold text-foreground">{formatCurrency(totalPending)}</p>
                </div>
                <div className="rounded-lg border border-border p-3 text-sm text-foreground">
                  {approvedPayments.length} aprovados, {pendingPayments.length} pendentes e {failedPayments.length} falhos.
                </div>
              </div>
            ) : loadingPlanos ? (
              <p className="text-sm text-muted-foreground">Carregando planos...</p>
            ) : planosAtivos.length === 0 ? (
              <p className="text-sm text-muted-foreground">Não há planos ativos no momento.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {planosAtivos.map((plano) => (
                  <div key={plano.id} className="rounded-xl border border-border p-4">
                    <p className="text-sm font-semibold text-foreground">{plano.nome}</p>
                    <p className="text-sm font-bold text-foreground mt-2">{formatCurrency(plano.valor || 0)}/mes</p>
                    <Button className="w-full mt-3" onClick={() => handleAssinarPlano(plano)}>
                      Assinar
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        );
      case "perfil":
        return (
          <div className="space-y-4">
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Meu Perfil
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Nome</label>
                <input
                  value={perfilForm.nome}
                  onChange={(e) => setPerfilForm((prev) => ({ ...prev, nome: e.target.value }))}
                  disabled={!editingPerfil}
                  className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-sm disabled:bg-muted"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Sobrenome</label>
                <input
                  value={perfilForm.sobrenome}
                  onChange={(e) => setPerfilForm((prev) => ({ ...prev, sobrenome: e.target.value }))}
                  disabled={!editingPerfil}
                  className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-sm disabled:bg-muted"
                />
              </div>
            </div>
            <div className="mt-3">
              <label className="text-xs text-muted-foreground">E-mail</label>
              <input
                value={perfil.email || "Nao informado"}
                disabled
                className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-sm bg-muted"
              />
            </div>
            <div className="mt-4 flex gap-2">
              {!editingPerfil ? (
                <Button onClick={() => setEditingPerfil(true)}>Editar</Button>
              ) : (
                <>
                  <Button onClick={handleSavePerfil} disabled={savingPerfil}>
                    {savingPerfil ? "Salvando..." : "Salvar"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setPerfilForm({ nome: perfil.nome, sobrenome: perfil.sobrenome });
                      setEditingPerfil(false);
                    }}
                  >
                    Cancelar
                  </Button>
                </>
              )}
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="text-lg font-semibold text-foreground mb-1">
              Meus documentos
            </h3>
            <p className="mb-4 text-xs text-muted-foreground">
              Documentos enviados na verificação de identidade. A selfie é a sua foto de perfil.
            </p>
            <DocumentsViewer user={perfil} role="familia" />
          </Card>
          </div>
        );
      default:
        return (
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Conteúdo da aba
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Esta seção está sendo preparada. Volte para o início para ver os dados principais.
            </p>
            <Button onClick={() => setActiveTab("inicio")}>Voltar para início</Button>
          </Card>
        );
    }
  };

  return (
    <AppShell
      role="familia"
      perfil={perfil}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <div className="p-6">
        <div className="mb-8">
          <h1
            className={cn(
              "font-heading text-3xl font-bold",
              "text-foreground"
            )}
          >
            Dashboard Família
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Gerencie seus agendamentos e encontre babás
          </p>
        </div>

        {renderContent()}
      </div>

      <ModalAvaliacao
        isOpen={modalAval.open}
        onClose={() => setModalAval({ open: false, solicitacao: null })}
        solicitacao={modalAval.solicitacao}
        familiaId={userId}
        onAvaliada={handleAvaliada}
      />
    </AppShell>
  );
}
